import { useParams } from "@solidjs/router";
import type { Room } from "sdk";
import {
	createMemo,
	createSelector,
	createSignal,
	For,
	Index,
	Match,
	Show,
	Switch,
} from "solid-js";
import { useChannels, useRooms } from "@/api";
import { useCtx } from "@/app/context";
import icFolder1 from "@/assets/folder-1.png";
import icHome from "@/assets/home.png";
import { Icon } from "@/atoms/Icon";
import { createTooltip } from "@/atoms/Tooltip";
import { useMenu } from "@/contexts/mod";
import { useNavigate } from "@/contexts/router";
import { useCurrentRoomId } from "@/hooks/useCurrentRoomId";
import { useRoomDnd } from "@/hooks/useRoomDnd";
import {
	type RoomNavFocusItem,
	useRoomNavKeybinds,
} from "@/hooks/useRoomNavKeybinds";
import { flags } from "@/lib/flags";
import { getLastViewedChannel } from "@/lib/room-nav";
import type { RoomNavConfig } from "@/types/room-nav";
import { RoomIcon } from "./User";

export const RoomNav = () => {
	const rooms2 = useRooms();
	const channels2 = useChannels();
	const ctx = useCtx();
	const { setMenu } = useMenu();
	const rooms = createMemo(() => [...rooms2.cache.values()]);

	const roomMentionCounts = createMemo(() => {
		const counts = new Map<string, number>();
		for (const channel of channels2.cache.values()) {
			if (channel.room_id && channel.mention_count) {
				counts.set(
					channel.room_id,
					(counts.get(channel.room_id) ?? 0) + channel.mention_count,
				);
			}
		}
		return counts;
	});

	const getRoomMentionCount = (roomId: string) =>
		roomMentionCounts().get(roomId) ?? 0;

	const getRoomUnread = (roomId: string) => {
		for (const channel of channels2.cache.values()) {
			if (
				channel.room_id === roomId &&
				channel.is_unread &&
				channel.type !== "Voice"
			) {
				return true;
			}
		}
		return false;
	};

	const getFolderUnread = (folder: { items: Room[] }) => {
		return folder.items.some((room) => getRoomUnread(room.id));
	};

	// TODO: render folder mention count
	const _getFolderMentionCount = (folder: { items: Room[] }) => {
		return folder.items.reduce(
			(acc, room) => acc + getRoomMentionCount(room.id),
			0,
		);
	};

	const getConfig = (): RoomNavConfig => {
		const config = ctx.preferences().frontend.roomNav as RoomNavConfig;
		if (config && Array.isArray(config)) {
			return JSON.parse(JSON.stringify(config)); // Deep copy
		}
		return [];
	};

	const updateRoomOrder = (newConfig: RoomNavConfig) => {
		for (const item of newConfig) {
			if (item.type === "folder" && !item.id) {
				item.id = crypto.randomUUID();
			}
		}
		const c = ctx.preferences();
		ctx.setPreferences({
			...c,
			frontend: {
				...c.frontend,
				roomNav: newConfig,
			},
		});
	};

	const dnd = useRoomDnd({
		getConfig,
		setConfig: updateRoomOrder,
		getDefaultConfig: () =>
			rooms().map((r) => ({ type: "room", room_id: r.id })),
		rooms,
	});

	const [collapsedFolders, setCollapsedFolders] = createSignal(
		new Set<string>(),
	);

	const toggleFolder = (id: string) => {
		setCollapsedFolders((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const navItems = createMemo(() => {
		const items: RoomNavFocusItem[] = [];
		items.push({ id: "home", type: "home", folderId: null });

		for (const item of dnd.previewedItems()) {
			if (item.type === "folder" && item.id) {
				items.push({ id: item.id, type: "folder", folderId: null });
				if (!collapsedFolders().has(item.id)) {
					for (const room of item.items) {
						items.push({ id: room.id, type: "room", folderId: item.id });
					}
				}
			} else if (item.type === "view" && item.name) {
				items.push({ id: item.id, type: "view", folderId: null });
			} else if (item.type === "room" && item.room) {
				items.push({ id: item.room.id, type: "room", folderId: null });
			}
		}

		return items;
	});

	const params = useParams();
	const currentRoomId = useCurrentRoomId();
	const selectedNavId = () => params.view_id ?? (currentRoomId() || "home");

	const nav = useNavigate();

	const keybinds = useRoomNavKeybinds({
		items: navItems,
		selectedId: selectedNavId,
		onToggleFolder: toggleFolder,
		onSelectRoom: (id) => {
			if (id === "home") {
				nav("/");
				return;
			}

			// jump straight to the last channel the user viewed in this room
			const lastChannelId = getLastViewedChannel(id);
			const lastChannel = lastChannelId
				? channels2.cache.get(lastChannelId)
				: undefined;
			if (
				lastChannel &&
				!lastChannel.deleted_at &&
				lastChannel.room_id === id
			) {
				nav(`/channel/${lastChannelId}`);
				return;
			}

			nav(`/room/${id}`);
		},
	});

	const isFocused = (id: string) => {
		const focused = keybinds.focusedId();
		if (focused !== null) return focused === id;
		const selected = selectedNavId();
		return selected === id;
	};

	const isSelected = createSelector(selectedNavId);

	const RoomItem = (props: { room: Room; folderId?: string }) => {
		const mentionCount = () => getRoomMentionCount(props.room.id);

		const tip = createTooltip({
			tip: () => (
				<div style="text-align: left">
					<div>{props.room.name}</div>
					<Show when={false /* TODO: online/member counts */}>
						<div class="dim">
							{props.room.online_count}/{props.room.member_count} online
						</div>
					</Show>
				</div>
			),
			placement: "right",
		});

		return (
			<li
				draggable="true"
				class="item menu-room room-item"
				data-id={props.room.id}
				data-room-id={props.room.id}
				data-folder-id={props.folderId}
				data-type="room"
				data-nav-id={props.room.id}
				tabIndex={isFocused(props.room.id) ? 0 : -1}
				onDragStart={dnd.handle}
				onDragOver={dnd.handle}
				onDragLeave={dnd.handle}
				onDrop={dnd.handle}
				onDragEnd={dnd.handle}
				classList={{
					dragging: dnd.dragging()?.id === props.room.id,
					unread: getRoomUnread(props.room.id),
					selected: isSelected(props.room.id),
				}}
				ref={tip.content}
			>
				<div class="tile" data-focus-ring-target>
					<a
						draggable="false"
						href={`/room/${props.room.id}`}
						class="nav"
						tabIndex={-1}
					>
						<RoomIcon room={props.room} mentionCount={mentionCount()} />
					</a>
				</div>
			</li>
		);
	};

	const homeTip = createTooltip({
		tip: () => (
			<div style="text-align: left">
				<div>home</div>
			</div>
		),
		placement: "right",
	});

	return (
		<Show when={flags.has("two_tier_nav")}>
			<nav id="room-nav" ref={keybinds.container} tabindex="-1">
				<ul class="room-list">
					<li
						class="item room-item"
						classList={{ selected: isSelected("home") }}
						data-nav-id="home"
						tabIndex={isFocused("home") ? 0 : -1}
						ref={homeTip.content}
					>
						<div class="tile with-background" data-focus-ring-target>
							<a href="/" tabIndex={-1}>
								<Icon src={icHome} alt="home" />
							</a>
						</div>
					</li>

					<For
						each={dnd.previewedItems()}
						fallback={
							<Index each={Array(10).fill(0)}>
								{(_, index) => (
									<li
										class="item room-item"
										style={{
											"pointer-events": "none",
											opacity: 1 - index / 10,
										}}
									>
										<div class="tile with-background"> </div>
									</li>
								)}
							</Index>
						}
					>
						{(item) => (
							<Switch>
								<Match when={item.type === "folder" && item} keyed>
									{(folder) => {
										const tip = createTooltip({
											tip: () => (
												<div style="text-align: left">
													<div>{folder.name}</div>
												</div>
											),
											placement: "right",
										});

										return (
											<div
												class="folder"
												data-id={folder.id}
												data-type="folder"
												draggable="true"
												onDragStart={dnd.handle}
												onDragOver={dnd.handle}
												onDrop={dnd.handle}
												onDragLeave={dnd.handle}
												onDragEnd={dnd.handle}
												classList={{
													dragging: dnd.dragging()?.id === folder.id,
													target:
														dnd.target()?.id === folder.id &&
														dnd.target()?.position === "inside",
													collapsed: collapsedFolders().has(folder.id),
													unread: !!getFolderUnread(folder),
												}}
											>
												<div
													class="item folder-item"
													classList={{
														unread: !!getFolderUnread(folder),
													}}
													data-nav-id={folder.id}
													tabIndex={isFocused(folder.id) ? 0 : -1}
													ref={tip.content}
												>
													<div
														class="tile with-background"
														data-focus-ring-target
														onClick={() => toggleFolder(folder.id)}
														onContextMenu={(e) => {
															e.preventDefault();
															queueMicrotask(() => {
																setMenu({
																	x: e.clientX,
																	y: e.clientY,
																	type: "folder",
																	folder_id: folder.id,
																});
															});
														}}
													>
														<Icon src={icFolder1} alt="folder" />
													</div>
												</div>
												<Show when={!collapsedFolders().has(folder.id)}>
													<ul class="folder-items">
														<For each={folder.items}>
															{(room) => (
																<RoomItem room={room} folderId={folder.id} />
															)}
														</For>
													</ul>
												</Show>
											</div>
										);
									}}
								</Match>
								<Match when={item.type === "view" && item} keyed>
									{(view) => (
										<li
											class="item menu-room"
											draggable="true"
											data-id={view.id}
											data-type="view"
											data-nav-id={view.id}
											tabIndex={isFocused(view.id) ? 0 : -1}
											onDragStart={dnd.handle}
											onDragOver={dnd.handle}
											onDragLeave={dnd.handle}
											onDrop={dnd.handle}
											onDragEnd={dnd.handle}
											onContextMenu={(e) => {
												e.preventDefault();
												queueMicrotask(() => {
													setMenu({
														x: e.clientX,
														y: e.clientY,
														type: "view",
														view_id: view.id,
													});
												});
											}}
											classList={{
												dragging: dnd.dragging()?.id === view.id,
												selected: isSelected(view.id),
											}}
										>
											<div class="tile with-background" data-focus-ring-target>
												<a
													draggable="false"
													href={`/view/${view.id}`}
													class="nav"
													tabIndex={-1}
												>
													<div class="avatar">{view.name.substring(0, 2)}</div>
												</a>
											</div>
										</li>
									)}
								</Match>
								<Match when={item.type === "room" && item} keyed>
									{(roomItem) => <RoomItem room={roomItem.room} />}
								</Match>
							</Switch>
						)}
					</For>
					<li
						class="drop-target-end"
						data-id="end"
						data-type="root"
						onDragOver={dnd.handle}
						onDrop={dnd.handle}
						style={{ height: "40px", "margin-top": "auto" }}
					/>
				</ul>
			</nav>
		</Show>
	);
};
